<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-vue-next"
import { PaginationListItem } from "reka-ui"

import { Button } from "@/components/ui/button"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
} from "@/components/ui/pagination"

const props = defineProps<{
  currentPage: number
  totalPages: number
  totalCount: number
  perPage: number
  baseUrl: string
  extraParams?: Record<string, string | number | null | undefined>
}>()

function goTo(page: number) {
  const clamped = Math.max(1, Math.min(props.totalPages, page))
  const params = Object.fromEntries(
    Object.entries({ ...props.extraParams, page: clamped }).filter(([, v]) => v != null && String(v) !== ""),
  )
  router.get(props.baseUrl, params, { preserveScroll: true })
}
</script>

<template>
  <div v-if="totalPages > 1" class="flex items-center justify-between gap-4 px-2 py-3">
    <p class="text-muted-foreground shrink-0 whitespace-nowrap text-sm">
      Showing {{ ((currentPage - 1) * perPage + 1).toLocaleString() }}–{{ Math.min(currentPage * perPage, totalCount).toLocaleString() }}
      of {{ totalCount.toLocaleString() }}
    </p>

    <Pagination
      class="w-auto"
      :page="currentPage"
      :total="totalCount"
      :items-per-page="perPage"
      :sibling-count="2"
      show-edges
      @update:page="goTo"
    >
      <PaginationContent v-slot="{ items }">
        <!-- First page -->
        <PaginationFirst size="icon" class="size-9 p-0">
          <ChevronsLeft class="size-4" />
        </PaginationFirst>

        <!-- Previous -->
        <Button
          variant="ghost"
          size="icon"
          class="size-9"
          :disabled="currentPage <= 1"
          @click="goTo(currentPage - 1)"
        >
          <ChevronLeft class="size-4" />
        </Button>

        <!-- Page numbers -->
        <template v-for="(item, index) in items" :key="index">
          <PaginationListItem v-if="item.type === 'page'" :value="item.value" as-child>
            <Button :variant="item.value === currentPage ? 'default' : 'ghost'" size="icon" class="h-9 w-auto min-w-9 px-2.5">
              {{ item.value }}
            </Button>
          </PaginationListItem>
          <PaginationEllipsis v-else :index="index" />
        </template>

        <!-- Next -->
        <Button
          variant="ghost"
          size="icon"
          class="size-9"
          :disabled="currentPage >= totalPages"
          @click="goTo(currentPage + 1)"
        >
          <ChevronRight class="size-4" />
        </Button>

        <!-- Last page -->
        <PaginationLast size="icon" class="size-9 p-0">
          <ChevronsRight class="size-4" />
        </PaginationLast>
      </PaginationContent>
    </Pagination>
  </div>
</template>
